import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { SlideshowSelectEvent } from '@theme/events';

const PDP_UPSELL_LIST_ID = 'pdp_upsell';
const PDP_UPSELL_LIST_NAME = 'Product Page Upsell';

class EqPdpTracking extends Component {
  #abortController = null;
  #context = null;
  #viewedProduct = null;
  #cartEventsSeen = new WeakSet();
  #redesignThumbnailIntent = new WeakMap();
  #galleryInteractionIntent = new WeakMap();
  #variantSelectIntent = 0;
  #mistiAddonNavigationIntent = new WeakMap();
  #mistiAddonLastNavigation = new WeakMap();

  connectedCallback() {
    super.connectedCallback();
    this.#context = this.#readJson('[data-eq-pdp-context]');
    this.#viewedProduct = this.#readJson('[data-eq-pdp-viewed-product]');

    this.trackViewedProduct();
    this.trackUpsellListView();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    document.addEventListener('pointerdown', this.#handleRedesignThumbnailIntent, { capture: true, signal });
    document.addEventListener('keydown', this.#handleRedesignThumbnailIntent, { capture: true, signal });
    document.addEventListener('click', this.#handleClick, { capture: true, signal });
    // On window so they run before Horizon's document-level on:click updates the quantity and
    // disables minus at 1 (or plus at the maximum).
    window.addEventListener('click', this.#handleQuantityClick, { capture: true, signal });
    window.addEventListener('click', this.#handleMistiClick, { capture: true, signal });
    document.addEventListener('change', this.#handleChange, { capture: true, signal });
    document.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    // Capture: the media gallery reacts to product:select and dispatches slideshow:select synchronously,
    // so the intent must be set before that, i.e. before the gallery's own (bubble-phase) product:select handler.
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { capture: true, signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });
    document.addEventListener('shopify:section:load', this.#handleSectionLoad, { signal });

    // Live's nk_misti_pdp_events.js runs on every PDP, so the six `pdp_*` buy-box events do too.
    document.addEventListener('pointerdown', this.#handleMistiAddonNavigationIntent, { capture: true, signal });
    document.addEventListener('wheel', this.#handleMistiAddonNavigationIntent, { capture: true, passive: true, signal });
    document.addEventListener('keydown', this.#handleMistiAddonNavigationIntent, { capture: true, signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    super.disconnectedCallback();
  }

  #readJson(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  #handleSectionLoad = () => {
    this.trackUpsellListView();
  };

  // eq-loop-misti-membership-tracking.js already sends pdp_add_to_cart and the FAQ events on its templates.
  #hasLoopMembershipTracker() {
    return Boolean(document.querySelector('eq-loop-misti-membership-tracking'));
  }

  #mainProductForm() {
    return this.#section().querySelector('.eq-product-information product-form-component') || null;
  }

  #mistiQuantity() {
    const form = this.#mainProductForm();
    const input = form?.querySelector('quantity-selector-component input[name="quantity"]');
    const quantity = Number(input?.value || form?.dataset.quantityDefault || 1);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  #mistiSelectedAddonSkus() {
    return this.#upsellInputs({ selectedOnly: true })
      .map((input) => String(input.dataset.upsellVariantSku || input.dataset.upsellVariantId || ''))
      .filter(Boolean)
      .join(',');
  }

  #mistiVisibleFaqCount() {
    const faq = document.querySelector('eq-new-pdp-redesign-faq');
    return faq
      ? Array.from(faq.querySelectorAll('.eq-new-pdp-redesign-faq__item'))
          .filter((item) => item.getClientRects().length > 0).length
      : 0;
  }

  #mistiMainItem() {
    const item = this.#mainItem();
    if (!item) return null;
    const variant = this.#context?.variants?.find((entry) => String(entry.id) === item.variant_id);
    const price = Number(variant?.price);
    return {
      ...item,
      unit_price: Number.isFinite(price) ? price : undefined,
    };
  }

  #mistiPush(eventName, fields) {
    this.#pushDataLayer({ event: eventName, ...fields });
  }

  #section() {
    return this.closest('.shopify-section') || document;
  }

  #selectedVariantId() {
    const input = this.#section().querySelector('product-form-component input[name="id"]');
    return input?.value ? String(input.value) : String(this.#context?.variants?.[0]?.id || '');
  }

  #mainItem() {
    const productId = String(this.#context?.product_id || '');
    const productName = String(this.#context?.product_name || '');
    const variantId = this.#selectedVariantId();
    const variant = this.#context?.variants?.find((entry) => String(entry.id) === variantId);

    if (!productId || !productName || !variantId) return null;

    return {
      item_id: String(variant?.sku || variantId),
      item_name: productName,
      product_name: productName,
      variant_id: variantId,
      product_id: productId,
    };
  }

  #pushDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  #pushPdpEvent(eventName, action, fields = {}, item = this.#mainItem()) {
    if (!eventName || !item) return;

    const payload = {
      action,
      event_name: eventName,
      page_path: window.location.pathname,
      direction: null,
      identity_scope: 'main_product',
      ...item,
      ...fields,
    };

    this.#pushDataLayer({ nk_pdp: null });
    this.#pushDataLayer({ event: eventName, nk_pdp: payload });
  }

  // The Klaviyo app embed tracks Viewed Product in <head> and leaves its payload on `window.item`.
  #klaviyoAppItem() {
    const item = window.item;
    return item && String(item.ProductID) === String(this.#viewedProduct?.ProductID) ? item : null;
  }

  trackViewedProduct() {
    const item = this.#viewedProduct;
    if (!item?.ProductID || this.#klaviyoAppItem()) return;

    const trackingKey = `${item.ProductID}:${item.URL || window.location.pathname}`;
    if (window.__eqPdpViewedProductKey === trackingKey) return;
    window.__eqPdpViewedProductKey = trackingKey;

    window._learnq = window._learnq || [];
    window._learnq.push(['track', 'Viewed Product', item]);
    window._learnq.push([
      'trackViewedItem',
      {
        Title: item.Name,
        ItemId: item.ProductID,
        Categories: item.Categories,
        ImageUrl: item.ImageURL,
        Url: item.URL,
        Metadata: {
          Brand: item.Brand,
          Price: item.Price,
          CompareAtPrice: item.CompareAtPrice,
        },
      },
    ]);
  }

  #upsellInputs({ selectedOnly = false } = {}) {
    const selector = selectedOnly
      ? '.eq-pdp-upsells [data-upsell-variant-id]:checked'
      : '.eq-pdp-upsells [data-upsell-variant-id]';
    return Array.from(this.#section().querySelectorAll(selector));
  }

  #upsellItem(input) {
    if (!(input instanceof HTMLInputElement)) return null;

    const itemId = String(input.dataset.upsellVariantId || '');
    const itemName = String(input.dataset.upsellItemName || '').trim();
    const productId = String(input.dataset.upsellProductId || '');
    const productName = String(input.dataset.upsellProductName || '').trim();
    const price = Number(input.dataset.upsellPrice);
    const index = Number(input.dataset.upsellIndex);

    if (!itemId || !itemName || !Number.isFinite(price)) return null;

    return {
      item_id: itemId,
      variant_id: itemId,
      item_name: itemName,
      product_id: productId || null,
      product_name: productName || itemName,
      price,
      quantity: 1,
      index: Number.isInteger(index) ? index : 0,
      item_list_id: PDP_UPSELL_LIST_ID,
      item_list_name: PDP_UPSELL_LIST_NAME,
    };
  }

  #pushUpsellEcommerce(eventName, items, extra = {}) {
    if (!items.length) return;

    const ga4Items = items.map((item) => ({
      item_id: item.item_id,
      item_name: item.item_name,
      price: item.price,
      quantity: item.quantity,
      index: item.index,
      item_list_id: PDP_UPSELL_LIST_ID,
      item_list_name: PDP_UPSELL_LIST_NAME,
    }));

    this.#pushDataLayer({ ecommerce: null });
    this.#pushDataLayer({
      event: eventName,
      item_list_id: PDP_UPSELL_LIST_ID,
      item_list_name: PDP_UPSELL_LIST_NAME,
      ecommerce: {
        item_list_id: PDP_UPSELL_LIST_ID,
        item_list_name: PDP_UPSELL_LIST_NAME,
        items: ga4Items,
        ...extra,
      },
    });
  }

  trackUpsellListView() {
    const items = this.#upsellInputs().map((input) => this.#upsellItem(input)).filter(Boolean);
    if (!items.length) return;

    const key = `${this.#context?.product_id || window.location.pathname}:${items.map((item) => item.item_id).join(',')}`;
    window.__eqPdpUpsellViewKeys = window.__eqPdpUpsellViewKeys || new Set();
    if (window.__eqPdpUpsellViewKeys.has(key)) return;
    window.__eqPdpUpsellViewKeys.add(key);

    this.#pushUpsellEcommerce('view_item_list_pdp_upsell', items);
  }


  #handleRedesignThumbnailIntent = (event) => {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element)) return;

    const galleryControl = event.target.closest(
      'media-gallery slideshow-controls[thumbnails] .slideshow-controls__thumbnail, media-gallery .slideshow-controls__dots .slideshow-control'
    );
    if (galleryControl && !galleryControl.disabled) {
      const mediaGallery = galleryControl.closest('media-gallery');
      const slideshow = mediaGallery?.querySelector('slideshow-component');
      if (slideshow) {
        this.#galleryInteractionIntent.set(slideshow, {
          type: galleryControl.closest('.slideshow-controls__dots') ? 'dot' : 'thumbnail',
          createdAt: performance.now(),
        });
      }
    }

    const thumbnail = event.target.closest('.eq-new-pdp-redesign-thumbnails__item');
    if (!thumbnail || thumbnail.disabled) return;

    const requestedIndex = Number(thumbnail.dataset.index);
    if (!Number.isInteger(requestedIndex)) return;

    const slideshow = thumbnail.closest('media-gallery')?.querySelector('slideshow-component');
    const previousIndex = Number(slideshow?.current);

    this.#redesignThumbnailIntent.set(thumbnail, {
      requestedIndex,
      previousIndex: Number.isInteger(previousIndex) ? previousIndex : null,
      wasSelected: thumbnail.getAttribute('aria-selected') === 'true',
      createdAt: performance.now(),
    });
  };

  #handleSlideshowSelect = (event) => {
    const slideshow = event.target instanceof Element ? event.target.closest('slideshow-component') : null;
    if (slideshow?.matches('.eq-pdp-upsells__carousel') && this.#section().contains(slideshow)) {
      this.#handleMistiAddonSlideSelect(event, slideshow);
      return;
    }
    if (!slideshow || !slideshow.closest('.eq-product-information media-gallery')) return;

    const detail = event.detail;
    if (!detail || detail.index === detail.previousIndex) return;

    let interactionType = null;
    let variantDriven = false;
    if (detail.userInitiated) {
      if (detail.trigger === 'drag' || detail.trigger === 'scroll') {
        interactionType = 'swipe';
      } else {
        const intent = this.#galleryInteractionIntent.get(slideshow);
        if (intent && performance.now() - intent.createdAt <= 2000) interactionType = intent.type;
      }
      this.#galleryInteractionIntent.delete(slideshow);
    } else if (this.#variantSelectIntent && performance.now() - this.#variantSelectIntent <= 2000) {
      // A variant change moves the gallery to the variant's featured image. Live does this by replaying a
      // thumbnail click (main-product.liquid's variant @click), so it reports interaction_type 'thumbnail'
      // and also product_image_select — match both. Horizon drives the slideshow directly instead, so the
      // select is not userInitiated; the recent product:select is what identifies it as variant-driven.
      this.#variantSelectIntent = 0;
      interactionType = 'thumbnail';
      variantDriven = true;
    }
    if (!interactionType) return;

    this.#pushGalleryInteraction(interactionType, detail.previousIndex, detail.index, detail.slide);

    if (variantDriven) {
      const slide = detail.slide instanceof Element ? detail.slide : null;
      const mediaId = slide?.querySelector('[data-media-id]')?.getAttribute('data-media-id') || null;
      this.#pushPdpEvent('product_image_select', 'thumbnail_select', {
        thumbnail_position: Number(detail.index) + 1,
        media_id: mediaId,
      });
    }
  };

  // Arms the variant-driven gallery event: the main product's variant picker dispatches product:select just
  // before it moves the gallery to the variant's featured image (consumed in #handleSlideshowSelect).
  // Product-card swatches dispatch product:select too, so only the main variant-picker arms it.
  #handleProductSelect = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const picker = target?.closest('variant-picker');
    if (!picker || picker.closest('product-card') || !this.#section().contains(picker)) return;
    this.#variantSelectIntent = performance.now();
  };

  #pushGalleryInteraction(interactionType, previousIndex, index, slide) {
    const from = Number(previousIndex);
    const to = Number(index);
    if (!interactionType || !Number.isInteger(from) || !Number.isInteger(to) || from === to) return;

    const item = this.#mainItem();
    if (!item) return;

    const slideElement = slide instanceof Element ? slide : null;
    const isVideo = Boolean(
      slideElement?.classList.contains('product-media-container--video') ||
        slideElement?.classList.contains('product-media-container--external_video') ||
        slideElement?.querySelector('video, iframe')
    );

    this.#pushDataLayer({
      event: 'pdp_gallery_interaction',
      location_id: 'pdp_gallery',
      interaction_type: interactionType,
      direction: to > from ? 'next' : 'prev',
      image_index: to + 1,
      is_video: String(isVideo),
      item_id: item.item_id,
    });
  }

  #handleQuantityClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const quantityButton = event.target.closest(
      '.eq-product-information product-form-component quantity-selector-component button[name="plus"], .eq-product-information product-form-component quantity-selector-component button[name="minus"]'
    );
    if (!quantityButton || quantityButton.disabled) return;
    const direction = quantityButton.getAttribute('name') === 'plus' ? 'plus' : 'minus';
    this.#pushPdpEvent(
      direction === 'plus' ? 'product_quantity_increase_click' : 'product_quantity_decrease_click',
      'quantity_click',
      { direction, interaction_outcome: 'intent_only' }
    );
  };

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const summary = event.target.closest('.eq-pdp-description-accordions summary.details__header');
    if (summary) {
      const details = summary.closest('details');
      const wasOpen = details?.open === true;
      window.setTimeout(() => {
        if (!wasOpen && details?.open) {
          this.#pushDataLayer({
            event: 'expand_description_pdp',
            section: summary.textContent?.replace(/\s+/g, ' ').trim() || '',
            product_name: String(this.#context?.product_name || ''),
            product_id: String(this.#context?.product_id || ''),
          });
        }
      }, 0);
      return;
    }

    const thumbnail = event.target.closest(
      '.eq-product-information media-gallery slideshow-controls[thumbnails] .slideshow-controls__thumbnail'
    );
    if (thumbnail && !thumbnail.disabled) {
      const controls = thumbnail.closest('slideshow-controls');
      const thumbnails = controls ? Array.from(controls.querySelectorAll('.slideshow-controls__thumbnail')) : [];
      const thumbnailPosition = thumbnails.indexOf(thumbnail) + 1;
      const slideshow = thumbnail.closest('slideshow-component');
      window.setTimeout(() => {
        const selectedSlide = slideshow?.querySelector('slideshow-slide[aria-hidden="false"]');
        const mediaId = selectedSlide?.querySelector('[data-media-id]')?.getAttribute('data-media-id') || null;
        this.#pushPdpEvent('product_image_select', 'thumbnail_select', {
          thumbnail_position: thumbnailPosition,
          media_id: mediaId,
        });
      }, 0);
      return;
    }

    const redesignThumbnail = event.target.closest('.eq-new-pdp-redesign-thumbnails__item');
    if (redesignThumbnail && !redesignThumbnail.disabled) {
      const intent = this.#redesignThumbnailIntent.get(redesignThumbnail);
      this.#redesignThumbnailIntent.delete(redesignThumbnail);

      // Horizon's declarative click handler can update aria-selected before this
      // capture listener runs, so use the pre-click intent recorded on pointerdown
      // (or Enter/Space keydown) to preserve live's "actual selection change" semantics.
      if (!intent || performance.now() - intent.createdAt > 2000 || intent.wasSelected) return;

      const { requestedIndex, previousIndex } = intent;
      const thumbnailGroup = redesignThumbnail.closest('eq-new-pdp-redesign-thumbnails');
      const thumbnails = thumbnailGroup
        ? Array.from(thumbnailGroup.querySelectorAll('.eq-new-pdp-redesign-thumbnails__item'))
        : [];
      const thumbnailPosition = thumbnails.indexOf(redesignThumbnail) + 1;
      const slideshow = redesignThumbnail.closest('media-gallery')?.querySelector('slideshow-component');

      window.setTimeout(() => {
        // Only track after the requested thumbnail is actually selected. A drag
        // that suppresses selection therefore cannot create a false event.
        if (redesignThumbnail.getAttribute('aria-selected') !== 'true') return;

        const selectedSlide = slideshow?.querySelector('slideshow-slide[aria-hidden="false"]');
        const mediaId = selectedSlide?.querySelector('[data-media-id]')?.getAttribute('data-media-id') || null;

        this.#pushPdpEvent('product_image_select', 'thumbnail_select', {
          thumbnail_position: thumbnailPosition > 0 ? thumbnailPosition : requestedIndex + 1,
          media_id: mediaId,
        });

        this.#pushGalleryInteraction('thumbnail', previousIndex, requestedIndex, selectedSlide);
      }, 0);
      return;
    }

    const upsellArrow = event.target.closest(
      '.eq-pdp-upsells__carousel .slideshow-control--previous, .eq-pdp-upsells__carousel .slideshow-control--next'
    );
    // Horizon's slideshow handler runs before this capture listener and, with infinite:false, sets the
    // boundary arrow's `disabled` property as part of the navigating click. A truly disabled <button> can't
    // emit a click, so any click reaching here was on an enabled arrow — gate on aria-disabled only, not the
    // post-navigation `.disabled`, or boundary navigations (the common case with few items) never track.
    // Live keeps the disabled guard because its Splide loop never disables its arrows.
    if (upsellArrow && upsellArrow.getAttribute('aria-disabled') !== 'true') {
      const slideshow = upsellArrow.closest('slideshow-component');
      const direction = upsellArrow.classList.contains('slideshow-control--previous') ? 'left' : 'right';
      window.setTimeout(() => {
        if (!slideshow) return;
        const items = this.#visibleUpsellItems(slideshow);
        const single = items.length === 1 ? items[0] : null;
        this.#pushPdpEvent(
          direction === 'left' ? 'product_upsell_previous' : 'product_upsell_next',
          'upsell_navigate',
          {
            direction,
            items,
            visible_count: items.length,
            identity_scope: single ? 'single_visible_item' : 'multiple_visible_items',
            item_id: single?.item_id || null,
            item_name: single?.item_name || null,
            product_name: single?.product_name || null,
            variant_id: single?.variant_id || null,
            product_id: single?.product_id || null,
            interaction_outcome: 'observed_stable_navigation',
          }
        );
      }, 350);
      return;
    }

    const reviewFilter = event.target.closest('[data-oke-widget] button[aria-controls="filter-options"]');
    if (reviewFilter) {
      const item = this.#mainItem();
      if (item) {
        const opening = reviewFilter.getAttribute('aria-expanded') !== 'true';
        this.#pushDataLayer({
          event: 'pdp_reviews_filter',
          location_id: 'pdp_reviews',
          action: opening ? 'open' : 'close',
          filter_type: 'panel',
          filter_value: opening ? 'open' : 'close',
          item_id: item.item_id,
        });
      }

      if (reviewFilter.getAttribute('aria-expanded') === 'false') {
        window.setTimeout(() => {
          if (reviewFilter.getAttribute('aria-expanded') === 'true') {
            this.#pushPdpEvent('product_review_filters_open', 'review_filters_open', {});
          }
        }, 0);
      }
      return;
    }

    const writeReview = event.target.closest('[data-oke-widget] .oke-w-writeReview');
    if (writeReview) {
      const item = this.#mainItem();
      if (item) {
        this.#pushDataLayer({
          event: 'pdp_write_review_click',
          location_id: 'pdp_reviews',
          item_id: item.item_id,
          item_name: item.item_name,
        });
      }
    }
  };

  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.closest('[data-oke-widget] #filter-options')) {
      const item = this.#mainItem();
      if (item && input.matches('input.oke-w-filterOption-checkbox')) {
        const stars = /(\d+)/.exec(input.getAttribute('name') || '');
        this.#pushDataLayer({
          event: 'pdp_reviews_filter',
          location_id: 'pdp_reviews',
          action: input.checked ? 'apply' : 'remove',
          filter_type: 'star_rating',
          filter_value: stars ? stars[1] : input.parentElement?.textContent?.replace(/\s+/g, ' ').trim() || '',
          item_id: item.item_id,
        });
      } else if (item && input.matches('input.oke-w-textSearchFilter-input')) {
        const term = (input.value || '').trim().slice(0, 50);
        this.#pushDataLayer({
          event: 'pdp_reviews_filter',
          location_id: 'pdp_reviews',
          action: term ? 'apply' : 'remove',
          filter_type: 'text_search',
          filter_value: term,
          item_id: item.item_id,
        });
      }
    }

    if (!input.matches('.eq-pdp-upsells [data-upsell-variant-id]')) return;

    const item = this.#upsellItem(input);
    if (!item) return;

    this.#pushPdpEvent(
      'product_upsell_selection_change',
      'upsell_selection_change',
      {
        selection_state: input.checked ? 'selected' : 'deselected',
        selector_position: item.index + 1,
        identity_scope: 'upsell_selector',
        interaction_outcome: 'observed_state_change',
      },
      {
        item_id: item.item_id,
        item_name: item.item_name,
        product_name: item.product_name,
        variant_id: item.variant_id,
        product_id: item.product_id,
      }
    );

    if (input.checked) this.#pushUpsellEcommerce('select_item_pdp_upsell', [item]);

    if (this.#section().contains(input)) {
      const parent = this.#mistiMainItem();
      if (parent) {
        this.#mistiPush('pdp_addon_select', {
          location_id: 'pdp_addons',
          action: input.checked ? 'select' : 'deselect',
          parent_item_id: parent.item_id,
          parent_item_name: parent.item_name,
          addon_item_id: String(input.dataset.upsellVariantSku || input.dataset.upsellVariantId || ''),
          addon_item_name: String(input.dataset.upsellProductName || input.dataset.upsellItemName || ''),
          addon_price: item.price,
          addon_position: item.index + 1,
        });
      }
    }
  };

  // Human carousel input is correlated to native Horizon's slideshow:select.
  // Resize/variant-programmatic slideshow movements must not become customer events.
  #handleMistiAddonNavigationIntent = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const slideshow = target.closest('.eq-pdp-upsells__carousel');
    if (!slideshow || !this.#section().contains(slideshow)) return;

    const control = target.closest('.slideshow-control');
    if (control && slideshow.contains(control)) {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      if (control.disabled || control.getAttribute('aria-disabled') === 'true') return;
      this.#mistiAddonNavigationIntent.set(slideshow, {
        type: control.closest('.slideshow-controls__dots') ? 'dot' : 'arrow',
        time: performance.now(),
      });
      return;
    }

    if (!target.closest('slideshow-slides')) return;
    if (event.type === 'keydown' && !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    this.#mistiAddonNavigationIntent.set(slideshow, { type: 'swipe', time: performance.now() });
  };

  #handleMistiAddonSlideSelect(event, slideshow) {
    const detail = event.detail;
    if (!detail?.userInitiated || detail.index === detail.previousIndex) return;
    const parent = this.#mistiMainItem();
    if (!parent) return;

    const now = performance.now();
    const last = this.#mistiAddonLastNavigation.get(slideshow);
    if (last?.index === detail.index && now - last.time < 700) return;

    const intent = this.#mistiAddonNavigationIntent.get(slideshow);
    const recentIntent = intent && now - intent.time < 1500;
    if (!recentIntent && detail.trigger !== 'drag') return;

    this.#mistiAddonLastNavigation.set(slideshow, { index: detail.index, time: now });
    this.#mistiAddonNavigationIntent.delete(slideshow);
    this.#mistiPush('pdp_addon_carousel_nav', {
      location_id: 'pdp_addons',
      interaction_type: recentIntent ? intent.type : 'swipe',
      direction: detail.index > detail.previousIndex ? 'next' : 'prev',
      page_index: detail.index + 1,
      item_id: parent.item_id,
    });
  }

  // MISTI's original GTM contract uses flat top-level fields and click intent.
  // Existing generic PDP events remain untouched and can fire alongside these distinct names.
  #handleMistiClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const clicked = event.target;
    const form = this.#mainProductForm();
    if (!form) return;

    const quantityButton = clicked.closest('quantity-selector-component button[name="plus"], quantity-selector-component button[name="minus"]');
    if (quantityButton && form.contains(quantityButton) && !quantityButton.disabled) {
      const button = quantityButton.name;
      window.setTimeout(() => {
        const item = this.#mistiMainItem();
        if (!item) return;
        this.#mistiPush('pdp_quantity_change', {
          location_id: 'pdp_buy_box',
          button,
          quantity: this.#mistiQuantity(),
          item_id: item.item_id,
          item_name: item.item_name,
        });
      }, 0);
      return;
    }

    const addButton = clicked.closest('product-form-component [ref="addToCartButton"]');
    const isAddClick = addButton && form.contains(addButton) && !addButton.disabled;
    if (isAddClick) {
      // Live's theme.liquid: on the click, with the Klaviyo app's own `item`, falling back to the theme's viewed product.
      const klaviyoItem = this.#klaviyoAppItem() ?? this.#viewedProduct;
      if (klaviyoItem) {
        window._learnq = window._learnq || [];
        window._learnq.push(['track', 'Added to Cart', klaviyoItem]);
      }
    }

    if (this.#hasLoopMembershipTracker()) return;

    if (isAddClick) {
      const item = this.#mistiMainItem();
      if (!item) return;
      const quantity = this.#mistiQuantity();
      const sellingPlanInput = form.querySelector('input[name="selling_plan"]:checked, input[name="selling_plan"]');
      const selectedLoopOption = document.querySelector(
        '.loop-widget-purchase-option-selected, [data-loop-widget-purchase-option][aria-checked="true"]'
      );
      const purchaseOption = selectedLoopOption
        ? selectedLoopOption.hasAttribute('data-loop-widget-onetime-purchase-option') ? 'one_time' : 'subscribe_and_save'
        : sellingPlanInput?.value ? 'subscribe_and_save' : 'one_time';
      const value = Number.isFinite(item.unit_price)
        ? Math.round(item.unit_price * quantity * 100) / 100 : undefined;

      this.#mistiPush('pdp_add_to_cart', {
        location_id: 'pdp_buy_box',
        add_source: 'main_button',
        currency: String(this.#context?.currency || 'GBP'),
        value,
        item_id: item.item_id,
        item_name: item.item_name,
        purchase_option: purchaseOption,
        quantity,
        addons: this.#mistiSelectedAddonSkus(),
      });
      return;
    }

    const faqQuestion = clicked.closest('eq-new-pdp-redesign-faq .eq-new-pdp-redesign-faq__question');
    if (faqQuestion) {
      const details = faqQuestion.closest('.eq-new-pdp-redesign-faq__item');
      if (!(details instanceof HTMLDetailsElement)) return;
      const item = this.#mistiMainItem();
      if (!item) return;
      const wasOpen = details.open;
      window.setTimeout(() => {
        this.#mistiPush('pdp_faq_toggle', {
          location_id: 'pdp_faq',
          action: details.open === wasOpen ? (wasOpen ? 'close' : 'open') : details.open ? 'open' : 'close',
          faq_question: faqQuestion.dataset.faqTitle || faqQuestion.textContent?.replace(/\s+/g, ' ').trim() || '',
          faq_position: Number(details.dataset.faqPosition || 0),
          item_id: item.item_id,
        });
      }, 400);
      return;
    }

    const faqMore = clicked.closest('eq-new-pdp-redesign-faq .eq-new-pdp-redesign-faq__load-more');
    if (faqMore && !faqMore.hidden && !faqMore.disabled) {
      const before = this.#mistiVisibleFaqCount();
      const item = this.#mistiMainItem();
      if (!item) return;
      window.setTimeout(() => {
        this.#mistiPush('pdp_faq_load_more', {
          location_id: 'pdp_faq',
          rows_before: before,
          rows_after: this.#mistiVisibleFaqCount(),
          item_id: item.item_id,
        });
      }, 400);
    }
  };

  #visibleUpsellItems(slideshow) {
    const viewport = slideshow.getBoundingClientRect();
    return Array.from(slideshow.querySelectorAll('.eq-pdp-upsells__slide'))
      .filter((slide) => {
        const rect = slide.getBoundingClientRect();
        return rect.right > viewport.left && rect.left < viewport.right && rect.bottom > viewport.top && rect.top < viewport.bottom;
      })
      .map((slide) => this.#upsellItem(slide.querySelector('[data-upsell-variant-id]')))
      .filter(Boolean);
  }

  #handleCartLinesUpdate = (event) => {
    if (this.#cartEventsSeen.has(event) || event.action !== 'add' || event.context !== 'product') return;

    const eventTarget = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    const mainProductForm = this.#section().querySelector('product-form-component');
    if (!eventTarget || eventTarget !== mainProductForm) return;

    this.#cartEventsSeen.add(event);

    const addedLines = Array.isArray(event.lines) ? event.lines : [];
    // The event normalises `merchandiseId` to `gid://shopify/ProductVariant/<id>`; the upsell inputs carry the bare id.
    const variantIdOf = (line) => String(line?.merchandiseId || '').split('/').pop();
    const addedIds = new Set(addedLines.map(variantIdOf));
    const selectedUpsells = this.#upsellInputs({ selectedOnly: true })
      .map((input) => this.#upsellItem(input))
      .filter((item) => item && addedIds.has(item.item_id));

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError || !selectedUpsells.length) return;

        const cartItems = Array.isArray(detail?.items) ? detail.items : [];
        const trackedItems = selectedUpsells.map((item) => {
          const line = addedLines.find((entry) => variantIdOf(entry) === item.item_id);
          const cartItem = cartItems.find((entry) => String(entry.variant_id || entry.id) === item.item_id);
          const quantity = Number(line?.quantity) || 1;
          const finalPrice = Number(cartItem?.final_price);
          return {
            ...item,
            quantity,
            price: Number.isFinite(finalPrice) ? finalPrice / 100 : item.price,
          };
        });

        const value = Math.round(trackedItems.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100) / 100;
        this.#pushUpsellEcommerce('add_to_cart_pdp_upsell', trackedItems, {
          currency: this.#context?.currency || 'GBP',
          value,
        });
      })
      .catch(() => {});
  };
}

if (!customElements.get('eq-pdp-tracking')) customElements.define('eq-pdp-tracking', EqPdpTracking);
